import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export type EventStatus = 'received' | 'processing' | 'processed' | 'failed';

export interface WebhookEvent {
  id: number;
  event_type: string;
  source: string | null;
  source_id: string | null;
  status: EventStatus | null;
  event_data: Record<string, unknown> | null;
  extra_data: Record<string, unknown> | null;
  created_at: string | null;
  processed_at: string | null;
  error: string | null;
  matched_count?: number;
}

interface WebhookListParams {
  source_id?: string;
  status?: string;
  event_type?: string;
  limit?: number;
}

export const webhooksApi = createApi({
  reducerPath: 'webhooksApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['WebhookEvent'],
  endpoints: (builder) => ({
    getWebhookEvents: builder.query<WebhookEvent[], WebhookListParams>({
      query: (params) => ({ url: '/events', params: { ...params, source: 'webhook' } }),
      transformResponse: (response: { events: WebhookEvent[] }) => response.events,
      providesTags: ['WebhookEvent'],
    }),
    getWebhookEvent: builder.query<WebhookEvent, number>({
      query: (id) => `/events/${String(id)}`,
      transformResponse: (response: { event: WebhookEvent }) => response.event,
      providesTags: (_r, _e, id) => [{ type: 'WebhookEvent', id }],
    }),
  }),
});

export const { useGetWebhookEventsQuery, useGetWebhookEventQuery } = webhooksApi;
